import { useState } from 'react';
import { Button, Card, Empty, Popconfirm, Select, Space, Tag, Typography, message } from 'antd';
import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AclPermission, OwnerType, Role } from '@never2busy/shared';
import { aclApi } from '../../../api/endpoints/acl';
import { UserSelect } from '../../../components/common/UserSelect';
import { useAuthStore } from '../../auth/stores/authStore';

const { Text } = Typography;

const PERMISSION_LABEL: Record<string, string> = {
  [AclPermission.READ]: 'Ver',
  [AclPermission.WRITE]: 'Editar',
  [AclPermission.DELETE]: 'Eliminar',
};

interface Props {
  accountId: number;
}

/**
 * Card de permisos por cuenta (solo ADMIN/GERENTE). Mientras no haya reglas la
 * cuenta es visible para todos; al agregar la primera regla queda restringida
 * (solo los usuarios listados + ADMIN/GERENTE + creador + asignado la ven).
 */
export function AccountPermissionsCard({ accountId }: Props): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<number | undefined>();
  const [permission, setPermission] = useState<AclPermission>(AclPermission.READ);

  const isManager = role === Role.ADMIN || role === Role.GERENTE;

  const { data, isLoading } = useQuery({
    queryKey: ['acl', OwnerType.ACCOUNT, accountId],
    queryFn: () => aclApi.list(OwnerType.ACCOUNT, accountId),
    enabled: isManager && !!accountId,
  });

  const grantMutation = useMutation({
    mutationFn: () => aclApi.grant(OwnerType.ACCOUNT, accountId, userId!, permission),
    onSuccess: () => {
      message.success('Permiso otorgado');
      setUserId(undefined);
      void queryClient.invalidateQueries({ queryKey: ['acl', OwnerType.ACCOUNT, accountId] });
    },
    onError: () => message.error('No se pudo otorgar el permiso'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => aclApi.revoke(id),
    onSuccess: () => {
      message.success('Permiso quitado');
      void queryClient.invalidateQueries({ queryKey: ['acl', OwnerType.ACCOUNT, accountId] });
    },
    onError: () => message.error('No se pudo quitar el permiso'),
  });

  if (!isManager) return null;

  const entries = data ?? [];

  return (
    <Card
      title={
        <Space>
          <LockOutlined />
          Permisos
        </Space>
      }
      style={{ marginBottom: 16 }}
      loading={isLoading}
    >
      {entries.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">Visible para todos (sin restricción)</Text>}
        />
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {entries.map((e) => (
            <li
              key={e.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              <span>
                {e.user.firstName} {e.user.lastName}{' '}
                <Tag>{PERMISSION_LABEL[e.permission] ?? e.permission}</Tag>
              </span>
              <Popconfirm
                title="¿Quitar permiso?"
                okText="Sí"
                cancelText="No"
                okButtonProps={{ danger: true }}
                onConfirm={() => revokeMutation.mutate(e.id)}
              >
                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </li>
          ))}
        </ul>
      )}

      <Space.Compact style={{ width: '100%', marginTop: 12 }}>
        <div style={{ flex: 1 }}>
          <UserSelect value={userId} onChange={setUserId} placeholder="Agregar usuario…" />
        </div>
        <Select
          value={permission}
          onChange={setPermission}
          style={{ width: 110 }}
          options={[
            { value: AclPermission.READ, label: 'Ver' },
            { value: AclPermission.WRITE, label: 'Editar' },
            { value: AclPermission.DELETE, label: 'Eliminar' },
          ]}
        />
        <Button
          type="primary"
          disabled={!userId}
          loading={grantMutation.isPending}
          onClick={() => grantMutation.mutate()}
        >
          Otorgar
        </Button>
      </Space.Compact>
    </Card>
  );
}
